import { ContainersServiceSelectors } from 'containers/modules/containersServiceModule';
import { DatabasesServiceSelectors } from 'databases/modules/DatabasesServiceModule';
import { InfraConstants } from 'infra/infraConstants';
import { InfraServiceSelectors } from 'infra/modules/infraServiceModule';
import R from 'ramda';
import * as RA from 'ramda-adjunct';
import bigdata from 'services/bigdata/service';
import containers from 'services/containers/service';
import databases from 'services/databases/service';
import gpu from 'services/gpu/service';
import infra from 'services/infra/service';
import { MachineLearningServiceSelectors } from 'services/machinelearning/models/MachineLearningServiceModule';
import machineLearning from 'services/machinelearning/service';
import marketplace from 'services/marketplace/service';
import storage from 'services/storage/service';
import { getStore } from 'store/store';
import features from 'system/features';
import { metrics } from 'system/libs/metrics';
import { ModuleHelper } from 'utilities/moduleHelper';
import { stateHelper } from 'utilities/stateHelper';
import { asyncHelper } from 'utils/asyncHelper';
import { McsErrors } from 'utils/McsErrors/McsErrors';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { once } from 'utils/once';
import { reduxHelper } from 'utils/reduxHelper';
import { BasicModuleState } from 'utils/Types';
import { YandexMetrica } from 'utils/YandexMetrica';

import { Environment } from 'Environment';
import { GlobalState } from 'reactApp/core/rootReducer';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule/CurrentUserModule';
import { PermissionsActions } from 'reactApp/modules/PermissionsModule/PermissionsModule';
import { RoutePermissionsSelector } from 'reactApp/modules/PermissionsModule/RoutesPermissionsModule';
import { ServiceApi } from 'reactApp/modules/ServiceModule/ServiceModule.api';
import * as ServiceTypes from 'reactApp/modules/ServiceModule/ServiceModule.types';
import { ServiceUtils } from 'reactApp/modules/ServiceModule/ServiceModule.utils';
import { WhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';
import { ToastNotify } from 'reactComponents/ToastNotify/ToastNotify';

export type ServiceSelectorsType =
    | typeof MachineLearningServiceSelectors
    | typeof InfraServiceSelectors
    | typeof DatabasesServiceSelectors
    | typeof ContainersServiceSelectors
    | typeof InfraServiceSelectors;

const allServicesModule = stateHelper.createModuleState('allServices', {
    hasError: false,
    isLoaded: false,
    isLoading: false,
} as BasicModuleState);

let resolveServicesLoadingPromise: Function;

export const servicesLoadingPromise = new Promise((resolve) => {
    resolveServicesLoadingPromise = resolve;
});

const loadServices = (pid: string) => async (dispatch, getState) => {
    if (getIsLoaded(getState())) {
        resolveServicesLoadingPromise();
    } else {
        dispatch(allServicesModule.setPart('isLoading', true));
    }

    const data = await ServiceApi.loadServices(pid);
    const servicesData = _filterServicesDataForWhiteLabel(
        getState(),
        data.info_all,
    );

    const servicesStates = getAllServices(getState());

    R.forEachObjIndexed((service, key: ServiceTypes.InternalServiceName) => {
        const actions = AllServicesActions[key];

        const apiKey = ServiceUtils.transformInternalServiceNameToApi(key);
        const serviceData = servicesData[apiKey];
        if (serviceData && ServiceUtils.isActivated(serviceData)) {
            dispatch(actions.setServiceData(serviceData));
        }
    }, servicesStates);

    resolveServicesLoadingPromise();

    dispatch(
        allServicesModule.mergeModuleState({
            isLoaded: true,
            isLoading: false,
        }),
    );
};

const activateServices = (pid: string) => async (dispatch, getState) => {
    try {
        if (
            hasActivatedService(getState()) ||
            !features.isEnabled('NEW_ACTIVATION_API')
        ) {
            await dispatch(activateServicesByMultipleRequests(pid));
        } else {
            await ServiceApi.activateServices(pid);
        }
    } finally {
        // может быть 500, но сервисы все равно подключатся
        await dispatch(loadServices(pid));
        await dispatch(PermissionsActions.loadPermissions(pid));
    }

    const unactivatedServices = getUnactivatedServices(getState());

    const unactivatedServicesNames = Object.keys(
        unactivatedServices,
    ).filter((apiKey: ServiceTypes.ApiServiceName) =>
        filterByPermitted(getState(), apiKey),
    );

    if (unactivatedServicesNames.length) {
        throw new Error(
            `Не удалось подключить сервисы: ${unactivatedServicesNames.join(
                ', ',
            )}. ${McsErrors.TRY_LATER_HTML}`,
        );
    } else {
        ToastNotify.success('Сервисы успешно активированы.');
        sendMetricsOnActivate(
            'include_services',
            CurrentUserSelectors.getEmailUID(getState()),
        );
    }
};

function filterByPermitted(
    state: GlobalState,
    apiKey: ServiceTypes.ApiServiceName,
) {
    return RoutePermissionsSelector.getIsRoutePermitted(
        state,
        ServiceUtils.internalServiceNameToId[apiKey],
    );
}

function hasActivatedService(state: GlobalState) {
    return Object.keys(getActivatedServices(state)).length;
}

function sendMetricsOnActivate(action, emailUid) {
    once(action + emailUid, () => {
        metrics.send({
            eventName: 'PlatformEvents',
            category: 'mcsplatform',
            action,
        });
        YandexMetrica.reachGoal(action);
    });
}

const activateServicesByMultipleRequests = (pid) => async (
    dispatch,
    getState,
) => {
    const unactivatedServices = getUnactivatedServices(getState());
    let unactivatedServicesApiKeys = Object.keys(unactivatedServices).map(
        ServiceUtils.transformInternalServiceNameToApi,
    );

    if (
        unactivatedServicesApiKeys.includes('infra') &&
        hasPaaS(unactivatedServicesApiKeys)
    ) {
        /* для PaaS(databases, containers, bigdata, marketplace, gpu) сначала надо подключить infra */
        await ServiceApi.activateService(pid, 'infra');
        unactivatedServicesApiKeys = unactivatedServicesApiKeys.filter(
            (apiKey) => apiKey !== 'infra',
        );
    }

    if (Environment.isPrivateBuild) {
        unactivatedServicesApiKeys = unactivatedServicesApiKeys.filter(
            (apiKey) => filterByPermitted(getState(), apiKey),
        );
    }

    await Promise.all([
        unactivatedServicesApiKeys.map((apiKey) =>
            ServiceApi.activateService(pid, apiKey),
        ),
    ]);
};

function hasPaaS(servicesApiKeys: ServiceTypes.ApiServiceName[]) {
    return R.intersection<ServiceTypes.ApiServiceName>(servicesApiKeys, [
        'databases',
        'containers',
        'bigdata',
        'marketplace',
        'gpu',
    ]).length;
}

const waitServiceActivation = (serviceName) => async (dispatch, getState) => {
    const pid = CurrentUserSelectors.getPid(getState());

    if (ServiceSelectors.getActivatedServices(getState())[serviceName]) {
        return;
    }

    const RETRY_DELAY = 500;
    const MAX_ATTEMP_COUNT = 15;

    for (let attempt = 1; attempt <= MAX_ATTEMP_COUNT; attempt++) {
        await asyncHelper.delay(attempt * RETRY_DELAY);
        await dispatch(ServiceActions.loadServices(pid));

        if (ServiceSelectors.getActivatedServices(getState())[serviceName]) {
            const serviceModel = MODELS[serviceName];
            await dispatch(PermissionsActions.loadPermissions(pid));

            serviceModel.trigger('permissionsLoaded');
            break;
        }
    }
};

const notifyWhenServiceReleased = (serviceName: string) => (
    dispatch,
    getState,
) => {
    const pid = CurrentUserSelectors.getPid(getState());

    return ServiceApi.notifyWhenServiceReleased(pid, serviceName);
};

const _filterServicesDataForWhiteLabel = (
    state: GlobalState,
    servicesData: ServiceTypes.AllServicesInfo,
): Partial<ServiceTypes.AllServicesInfo> => {
    const availableServices = WhiteLabelSelectors.getAvailableServicesApiNames(
        state,
    );
    if (availableServices) {
        servicesData = RA.omitBy(
            (_, key: ServiceTypes.ApiServiceName) =>
                !availableServices.includes(key),
            servicesData,
        );
    }
    return servicesData;
};

export const { removeConsumer, addConsumer } = new ModuleListReloader({
    loadList: loadServices,
    updateTimeout: InfraConstants.LONG_UPDATE_INTERVAL,
});

export const ServiceActions = {
    notifyWhenServiceReleased,
    loadServices,
    activateServices,
    waitServiceActivation,
    addConsumer,
    removeConsumer,
};
export const BoundServiceActions = reduxHelper.bindActions<
    typeof ServiceActions
>(ServiceActions);

const getActivatedServices = (state: GlobalState) =>
    R.pipe(
        getAllServices,
        R.filter(ServiceUtils.isActivated),
    )(state) as ReturnType<typeof getAllServices>;

function getUnactivatedServices(state: GlobalState) {
    return R.filter(ServiceUtils.isUnactivated, getAllServices(state));
}

const getProtectedServices = (state: GlobalState) =>
    R.pipe(getAllServices, R.filter(ServiceUtils.isProtected))(state);

const hasNoProtectedServices = (state: GlobalState) =>
    R.isEmpty(getProtectedServices(state));

export const getAllServices = (state: GlobalState) =>
    R.mapObjIndexed((selectors) => {
        return selectors.getModuleState(state);
    }, AllServicesSelectors) as {
        [P in ServiceTypes.InternalServiceName]: ReturnType<
            typeof AllServicesSelectors.infra.getModuleState
        >;
    }; // need to use this "as ...", because otherwise we got type of result is object with any string as key

export const getAllServicesComputedData = (
    state: GlobalState,
    options?: ServiceTypes.SelectorOptions,
) =>
    R.pipe(
        R.omit(options?.excludeServices || []) as AllServiceSelectorsFuncType,
        R.mapObjIndexed((selectors: ReturnType<typeof getServiceSelectors>) => {
            return selectors.getComputedData(state);
        }),
    )(AllServicesSelectors) as {
        [P in ServiceTypes.InternalServiceName]: ReturnType<
            typeof AllServicesSelectors.infra.getComputedData
        >;
    }; // need to use this "as ...", because otherwise we got type of result is object with any string as key

function getIsLoaded(state: GlobalState) {
    return allServicesModule.getPart(state, 'isLoaded');
}

function getFrozenServices(
    state: GlobalState,
    options?: ServiceTypes.SelectorOptions,
) {
    return Object.values(getAllServicesComputedData(state, options)).filter(
        (serviceData) => serviceData.isFrozen,
    );
}

function hasFrozenService(
    state: GlobalState,
    options?: ServiceTypes.SelectorOptions,
) {
    return getFrozenServices(state, options).length !== 0;
}

function getSuspendedServices(
    state: GlobalState,
    options?: ServiceTypes.SelectorOptions,
) {
    return Object.values(getAllServicesComputedData(state, options)).filter(
        (serviceData) => serviceData.isSuspended,
    );
}

function hasSuspendedService(
    state: GlobalState,
    options?: ServiceTypes.SelectorOptions,
) {
    return getSuspendedServices(state, options).length !== 0;
}

export const ServiceSelectors = {
    getActivatedServices,
    getProtectedServices,
    hasNoProtectedServices,
    getAllServices,
    getAllServicesComputedData,
    getIsLoaded,
    getSuspendedServices,
    getFrozenServices,
    hasActivatedService,
    hasFrozenService,
    hasSuspendedService,
};

const MODELS = {
    infra,
    databases,
    containers,
    marketplace,
    storage,
    bigdata,
    gpu,
    machineLearning,
};

const getModule = ModuleHelper.wrapMultimodule(
    (serviceName: ServiceTypes.InternalServiceName) => {
        const serviceModel = MODELS[serviceName];

        const module = stateHelper.getMultiModule<ServiceTypes.ServiceData>({
            name: 'services',
            key: serviceName,
            initialState: {
                status: 'UNACTIVATED',
            },
        });

        serviceModel.on('change activation', (model) => {
            const currentState = module.getModuleState(getStore().getState());
            const data = model && model.toJSON && model.toJSON();
            if (!data) {
                return;
            }
            const hasChanges = Object.entries(data as typeof currentState).some(
                ([key, value]) => {
                    return currentState[key] !== value;
                },
            );
            if (hasChanges) {
                getStore().dispatch(module.mergeModuleState(data));
            }
        });

        const getStatus = module.makePartGetter('status');

        const getIsActivated = R.pipe(
            module.getModuleState,
            ServiceUtils.isActivated,
        );
        const getIsFrozen = R.pipe(
            module.getModuleState,
            ServiceUtils.isFrozen,
        );
        const getIsSuspended = R.pipe(
            module.getModuleState,
            ServiceUtils.isSuspended,
        );
        const getSuspendDateString = R.pipe(
            module.getModuleState,
            ServiceUtils.getSuspendDateString,
        );
        const getIsProtected = R.pipe(
            module.getModuleState,
            ServiceUtils.isProtected,
        );
        const getIsAvailableForClient = R.pipe(
            WhiteLabelSelectors.getAvailableServicesInternalNames,
            (names) => {
                if (!names) {
                    return true;
                }
                return names.includes(serviceName);
            },
        );
        const getStatusMessage = (state: GlobalState) => {
            return ServiceUtils.getStatusMessage({
                humanReadableName: serviceName,
                suspendDate: getSuspendDateString(state),
                isFrozen: getIsFrozen(state),
            });
        };

        const getComputedData = (state: GlobalState) => ({
            isFrozen: getIsFrozen(state),
            isActivated: getIsActivated(state),
            isProtected: getIsProtected(state),
            isSuspended: getIsSuspended(state),
            isAvailableForClient: getIsAvailableForClient(state),
            statusMessage: getStatusMessage(state),
            suspendedDate: (module.getModuleState(
                state,
            ) as ServiceTypes.InfraData).suspended_at,
        });

        const Selectors = {
            getModuleState: module.getModuleState,

            getComputedData,

            getStatus,

            getIsActivated,
            getIsFrozen,
            getIsSuspended,
            getIsProtected,

            getIsAvailableForClient,
        };

        const setServiceData = (rawData: ServiceTypes.ServiceData) => async (
            dispatch,
        ) => {
            serviceModel.set(rawData);

            dispatch(
                module.mergeModuleState({
                    ...rawData,
                }),
            );
        };

        const Actions = {
            setServiceData,
        };

        return {
            Selectors,
            Actions,
        };
    },
);

export const getServiceSelectors = (
    serviceName: ServiceTypes.InternalServiceName,
) => {
    const { Selectors } = getModule(serviceName);
    return Selectors;
};

export const getServiceActions = (
    serviceName: ServiceTypes.InternalServiceName,
) => {
    const { Actions } = getModule(serviceName);
    return Actions;
};

export const AllServicesActions = R.mapObjIndexed(
    (_, key: ServiceTypes.InternalServiceName) => getServiceActions(key),
    MODELS,
) as {
    [P in ServiceTypes.InternalServiceName]: ReturnType<
        typeof getServiceActions
    >;
};

export const AllServicesSelectors = R.mapObjIndexed(
    (_, key: ServiceTypes.InternalServiceName) => getServiceSelectors(key),
    MODELS,
) as AllServiceSelectorsType;

type AllServiceSelectorsType = {
    [P in ServiceTypes.InternalServiceName]: ReturnType<
        typeof getServiceSelectors
    >;
};

type AllServiceSelectorsFuncType = (
    data: AllServiceSelectorsType,
) => AllServiceSelectorsType;

export const SERVICE_NAMES = R.mapObjIndexed((_, key) => key, MODELS) as {
    [P in ServiceTypes.InternalServiceName]: P;
};
